import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import {
  pickCharacterForHandle,
  pickCharacterForHandleSync,
  type CharacterPick,
} from "../lib/character";
import { voiceForHandle, type ClientVoice } from "../lib/voicePool";
import "./DonorFlow.css";

// Build the `/s/{slug}` permalink the donor will land on after a successful
// tip. Mirrors the server's slug shape in functions/lib/supporter-slug.ts:
//   logged in  -> u-{user_handle}
//   anonymous  -> a-{streamer_handle}-{donor_handle}
function buildSupporterSlug(opts: {
  loggedInHandle?: string;
  streamerHandle: string;
  donorName: string;
}): string {
  const norm = (s: string): string =>
    s.trim().toLowerCase().replace(/^@/, "").replace(/[^a-z0-9_-]/g, "");
  if (opts.loggedInHandle) return `u-${norm(opts.loggedInHandle)}`;
  const donor = norm(opts.donorName) || "anon";
  return `a-${norm(opts.streamerHandle)}-${donor}`;
}

const MESSAGE_MAX = 280;

interface StreamerProfile {
  handle: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  total_donations: number;
  factions?: PublicFaction[];
}

interface PublicFaction {
  id: string;
  slug: string;
  name: string;
  color: string;
}

interface DonationCreateResp {
  donation_id: string;
  wall_url: string;
  status: "paid" | "pending";
}

type Step = "compose" | "confirm" | "done";

const AMOUNT_PRESETS = [5, 10, 25, 100];
const MIN_DONATION_USD = 5;

// Map dollars → totem tier. Matches `public/overlay/overlay.js` TIERS.
function tierFor(amount: number): { name: string; cls: string; life: string } {
  if (amount >= 500) return { name: "LEVIATHAN", cls: "t-leviathan", life: "~30 min" };
  if (amount >= 100) return { name: "WHALE",     cls: "t-whale",     life: "~20 min" };
  if (amount >= 20)  return { name: "SHARK",     cls: "t-shark",     life: "~10 min" };
  if (amount >= 5)   return { name: "GUPPY",     cls: "t-guppy",     life: "~5 min" };
  return { name: "MINNOW", cls: "t-minnow", life: "~3 min" };
}

// Faction slug fallbacks if streamer has no custom factions (e.g. mock data).
const DEFAULT_FACTIONS: PublicFaction[] = [
  { id: "crim", slug: "crim", name: "CRIMSON", color: "#FF1744" },
  { id: "cob",  slug: "cob",  name: "COBALT",  color: "#2979FF" },
  { id: "tox",  slug: "tox",  name: "TOXIC",   color: "#76FF03" },
  { id: "volt", slug: "volt", name: "VOLT",    color: "#FFEA00" },
  { id: "myth", slug: "myth", name: "MYTHIC",  color: "#D500F9" },
];

export default function DonorFlow(): JSX.Element {
  const { handle = "" } = useParams<{ handle: string }>();
  const user = useSession((s) => s.user);

  const streamerQ = useQuery({
    queryKey: ["streamer", handle],
    queryFn: () => api.get<StreamerProfile>(`/api/streamer/${handle}`),
    enabled: handle.length > 0,
  });

  const factions = useMemo<PublicFaction[]>(() => {
    const fromApi = streamerQ.data?.factions;
    if (fromApi && fromApi.length > 0) return fromApi;
    return DEFAULT_FACTIONS.slice(0, 3);
  }, [streamerQ.data]);

  const [step, setStep] = useState<Step>("compose");
  const [donorName, setDonorName] = useState<string>("");
  const [amount, setAmount] = useState<number>(25);
  const [factionId, setFactionId] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [characterPrompt, setCharacterPrompt] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [wallUrl, setWallUrl] = useState<string>("");

  // Deterministic character pick keyed off donor handle / typed name.
  // Same handle → same character forever, so returning supporters keep
  // their identity on the wall.
  const [character, setCharacter] = useState<CharacterPick>(() =>
    pickCharacterForHandleSync(donorName || "anon"),
  );
  useEffect(() => {
    let cancelled = false;
    pickCharacterForHandle(donorName || "anon").then((c) => {
      if (!cancelled) setCharacter(c);
    });
    return () => {
      cancelled = true;
    };
  }, [donorName]);

  // Default donor name from session, default faction = first.
  useEffect(() => {
    if (user && !donorName) setDonorName("@" + user.handle);
  }, [user, donorName]);
  useEffect(() => {
    if (factions.length > 0 && !factionId) setFactionId(factions[0].id);
  }, [factions, factionId]);

  const tier = tierFor(amount);
  const selectedFaction = factions.find((f) => f.id === factionId) ?? factions[0];
  const facSlug = selectedFaction?.slug ?? "crim";

  // Deterministic voice label based on the donor handle. Same FNV-1a algo as
  // the server pool, so the label here matches the voice the server will
  // persist on the first donation. TODO: play actual ElevenLabs preview clip.
  const voice = useMemo<ClientVoice>(
    () => voiceForHandle(donorName || "anon"),
    [donorName],
  );

  // Permalink the donor lands on after the tip clears. We compute it
  // client-side so the success screen can deep-link to /s/{slug} without an
  // extra round-trip; the server uses the same shape.
  const supporterSlug = useMemo(
    () =>
      buildSupporterSlug({
        loggedInHandle: user?.handle,
        streamerHandle: handle,
        donorName,
      }),
    [user, handle, donorName],
  );

  async function submit(): Promise<void> {
    if (amount < MIN_DONATION_USD) {
      setError(`Minimum donation is $${MIN_DONATION_USD} USD`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<DonationCreateResp>("/api/donations", {
        streamer_handle: handle,
        amount_usd: amount,
        donor_name: donorName.trim() || "anon",
        message: message.trim() || null,
        faction_id: factionId || null,
        character_id: character.id,
        character_prompt: characterPrompt.trim() || null,
      });
      setWallUrl(res.wall_url || `/${handle}?donated=1`);
      setStep("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="donor-flow">
      <div className="df-frame">
        <header className="df-head">
          <button
            className="df-back"
            onClick={() => (step === "compose" ? history.back() : setStep("compose"))}
            type="button"
          >
            ← back
          </button>
          <div className="df-streamer">
            {(streamerQ.data?.display_name || handle).toUpperCase()}
          </div>
          <div className="df-live">
            <span className="live-dot" />LIVE
          </div>
        </header>

        {step === "compose" && (
          <ComposeStep
            handle={handle}
            display={streamerQ.data?.display_name ?? handle}
            donorName={donorName}
            setDonorName={setDonorName}
            amount={amount}
            setAmount={setAmount}
            factions={factions}
            factionId={factionId}
            setFactionId={setFactionId}
            message={message}
            setMessage={setMessage}
            characterPrompt={characterPrompt}
            setCharacterPrompt={setCharacterPrompt}
            tier={tier}
            facSlug={facSlug}
            character={character}
            voice={voice}
            onNext={() => setStep("confirm")}
          />
        )}

        {step === "confirm" && (
          <ConfirmStep
            amount={amount}
            donorName={donorName || "anon"}
            facSlug={facSlug}
            facName={selectedFaction?.name || ""}
            tier={tier}
            message={message}
            character={character}
            voice={voice}
            submitting={submitting}
            error={error}
            onSubmit={submit}
          />
        )}

        {step === "done" && (
          <DoneStep
            handle={handle}
            display={streamerQ.data?.display_name ?? handle}
            amount={amount}
            donorName={donorName || "anon"}
            facSlug={facSlug}
            facName={selectedFaction?.name || ""}
            tier={tier}
            message={message}
            character={character}
            voice={voice}
            wallUrl={wallUrl}
            supporterSlug={supporterSlug}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Step 1 — Compose
// ============================================================
interface ComposeProps {
  handle: string;
  display: string;
  donorName: string;
  setDonorName: (v: string) => void;
  amount: number;
  setAmount: (v: number) => void;
  factions: PublicFaction[];
  factionId: string;
  setFactionId: (v: string) => void;
  message: string;
  setMessage: (v: string) => void;
  characterPrompt: string;
  setCharacterPrompt: (v: string) => void;
  tier: { name: string; cls: string; life: string };
  facSlug: string;
  character: CharacterPick;
  voice: ClientVoice;
  onNext: () => void;
}
function ComposeStep(p: ComposeProps): JSX.Element {
  return (
    <>
      <span className="df-v2-pill" aria-label="v2 live">v2 LIVE</span>
      <div className="df-hero">
        <h1>
          DROP A <span>TOTEM</span><br />ON {p.display.toUpperCase()}'S WALL
        </h1>
        <p>Pile up donations on stream · big blocks crush small ones</p>
      </div>

      <section className="df-card">
        <div className="df-lbl">WHO ARE YOU?</div>
        <input
          className="ph-input"
          value={p.donorName}
          onChange={(e) => p.setDonorName(e.target.value)}
          placeholder="@yourname"
          maxLength={32}
        />
      </section>

      <section className="df-card">
        <div className="df-lbl">HOW MUCH?</div>
        <div className="df-chips">
          {AMOUNT_PRESETS.map((v) => (
            <button
              key={v}
              type="button"
              className={`chip ${p.amount === v ? "active" : ""}`}
              onClick={() => p.setAmount(v)}
            >
              ${v}
            </button>
          ))}
          <button
            type="button"
            className={`chip ${!AMOUNT_PRESETS.includes(p.amount) ? "active" : ""}`}
            onClick={() => p.setAmount(50)}
          >
            OTHER
          </button>
        </div>
        <div className="ph-amount">
          <span className="dollar">$</span>
          <input
            className="amount-input"
            type="number"
            min={MIN_DONATION_USD}
            max={5000}
            value={p.amount}
            onChange={(e) =>
              p.setAmount(Math.max(MIN_DONATION_USD, Math.min(5000, Number(e.target.value) || MIN_DONATION_USD)))
            }
          />
        </div>
        <div className="df-min-hint">
          Tips start at ${MIN_DONATION_USD}. Bigger tips drop bigger totems and stick around longer.
        </div>
        <div className="df-tier-hint">
          {p.tier.name} TIER · {p.tier.life} life
        </div>
      </section>

      <section className="df-card">
        <div className="df-lbl">PICK A SIDE</div>
        <div className="df-factions">
          {p.factions.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`ph-faction f-${f.slug} ${p.factionId === f.id ? "active" : ""}`}
              onClick={() => p.setFactionId(f.id)}
              style={{ ["--c" as string]: f.color, ["--g" as string]: f.color }}
            >
              <div className="pf-g">▲</div>
              <div className="pf-n">{f.name}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="df-card">
        <div className="df-lbl">LEAVE A MESSAGE</div>
        <div className="df-msg-hint">
          this stays on their wall forever
        </div>
        <textarea
          className="ph-msg"
          value={p.message}
          onChange={(e) => p.setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          rows={3}
          placeholder="GG let's gooo"
        />
        <div className="df-msg-count">{p.message.length} / {MESSAGE_MAX}</div>
      </section>

      <section className="df-card df-char-card">
        <div className="df-lbl">YOUR CHARACTER &amp; VOICE</div>
        <div className="df-char">
          <CharacterPreview character={p.character} />
          <div className="df-char-meta">
            <div className="df-char-name">{p.character.label.toUpperCase()}</div>
            <div className="df-char-sub">
              picked from <span className="mono">@{(p.donorName || "anon").replace(/^@/, "")}</span>
            </div>
            <div className="df-char-sub df-char-note">
              same handle = same character, forever
            </div>
          </div>
        </div>
        <div className="df-voice">
          <span className="df-voice-icon" aria-hidden="true">▸</span>
          <span className="df-voice-label">
            your voice will be: <b>{p.voice.name}</b> &middot; {p.voice.vibe}
          </span>
        </div>
      </section>

      {/* AI-generated character — promoted to a hero card so first-time
          donors actually notice it. v2 differentiator for SubsWall. */}
      <section className="df-card df-ai-hero">
        <div className="df-ai-banner">
          <span className="df-ai-glyph" aria-hidden="true">⌬</span>
          <div>
            <div className="df-ai-eyebrow">DESIGN YOUR OWN · BETA</div>
            <div className="df-ai-title">
              Get a <span>one-of-one</span> AI character &amp; voice — forever.
            </div>
          </div>
        </div>
        <p className="df-ai-copy">
          Skip the default. Describe any character and we&apos;ll generate a
          unique mascot you keep across every wall you tip. First donation
          only — every future tip reuses your character.
        </p>
        <textarea
          className="df-ai-input"
          value={p.characterPrompt}
          onChange={(e) => p.setCharacterPrompt(e.target.value.slice(0, 280))}
          rows={3}
          placeholder="a chill goblin in a wizard hat, holding a tiny mug"
        />
        <div className="df-ai-foot">
          <span>{p.characterPrompt.length} / 280</span>
          <span>No IP, celebrities, or NSFW (auto-filtered).</span>
        </div>
      </section>

      <section className="df-preview">
        <div className="df-preview-label">LIVE PREVIEW</div>
        <div className={`totem ${p.tier.cls} f-${p.facSlug}`}>
          <div className="t-name">{(p.donorName || "@anon").toUpperCase()}</div>
          <div className="t-amt">${p.amount}</div>
        </div>
      </section>

      <div className="df-cta-wrap">
        <button
          className="df-cta"
          type="button"
          onClick={p.onNext}
          disabled={!p.donorName.trim() || !p.factionId || p.amount < MIN_DONATION_USD}
        >
          CONTINUE · ${p.amount} <span>▶</span>
        </button>
      </div>
    </>
  );
}

// ============================================================
// Step 2 — Confirm (preview + fake payment)
// ============================================================
interface ConfirmProps {
  amount: number;
  donorName: string;
  facSlug: string;
  facName: string;
  tier: { name: string; cls: string; life: string };
  message: string;
  character: CharacterPick;
  voice: ClientVoice;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
}
function ConfirmStep(p: ConfirmProps): JSX.Element {
  return (
    <div className="df-confirm">
      <div className="df-preview big">
        <div className="df-preview-label">YOUR TOTEM</div>
        <div className={`totem ${p.tier.cls} f-${p.facSlug}`}>
          <div className="t-name">{p.donorName.toUpperCase()}</div>
          <div className="t-amt">${p.amount}</div>
        </div>
        <div className="df-tier-hint">{p.tier.name} · {p.facName}</div>
        <div className="df-confirm-char">
          <CharacterPreview character={p.character} small />
          <span>{p.character.label}</span>
          <span className="df-confirm-voice">
            <span aria-hidden="true">▸</span> {p.voice.name} &middot; {p.voice.vibe}
          </span>
        </div>
        {p.message ? <p className="df-confirm-msg">"{p.message}"</p> : null}
      </div>

      <section className="df-card">
        <div className="df-lbl">PAYMENT METHOD</div>
        <div className="df-pays">
          <button type="button" className="pay-chip active">CARD</button>
          <button type="button" className="pay-chip" aria-disabled="true" disabled>APPLE PAY · SOON</button>
        </div>
        {import.meta.env.DEV ? (
          <div className="df-fakenote">fake payments enabled — no real charge</div>
        ) : null}
      </section>

      <div className="df-cta-wrap">
        <button
          className="df-cta"
          type="button"
          onClick={p.onSubmit}
          disabled={p.submitting}
        >
          {p.submitting ? "SIGNING…" : `SIGN THE WALL · $${p.amount}`}
          <span>▶</span>
        </button>
        {p.error ? <p className="df-error">{p.error}</p> : null}
      </div>
    </div>
  );
}

// ============================================================
// Step 3 — Done (confetti + link to overlay)
// ============================================================
interface DoneProps {
  handle: string;
  display: string;
  amount: number;
  donorName: string;
  facSlug: string;
  facName: string;
  tier: { name: string; cls: string; life: string };
  message: string;
  character: CharacterPick;
  voice: ClientVoice;
  wallUrl: string;
  supporterSlug: string;
}
function DoneStep(p: DoneProps): JSX.Element {
  return (
    <div className="df-done">
      <div className="df-confetti" aria-hidden="true">
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={i}
            className="confetti"
            style={{
              left: `${(i * 7) % 100}%`,
              animationDelay: `${(i * 73) % 1500}ms`,
              background: ["#FF1744", "#2979FF", "#76FF03", "#FFEA00", "#D500F9", "#9146FF"][i % 6],
            }}
          />
        ))}
      </div>

      <div className="df-landed">
        <div className={`totem ${p.tier.cls} f-${p.facSlug} totem-landed`}>
          <div className="t-name">{p.donorName.toUpperCase()}</div>
          <div className="t-amt">${p.amount}</div>
        </div>
      </div>

      <h2 className="df-done-h">
        YOUR TOTEM<br />
        LANDED ON <span>{p.display.toUpperCase()}'S WALL</span>
      </h2>
      {p.message ? <p className="df-done-msg">"{p.message}"</p> : null}

      <div className="df-done-stats">
        <div className="conf-stat">{p.tier.name} TIER ▲</div>
        <div className="conf-stat">DECAYS IN {p.tier.life.replace("~", "")}</div>
        <div className="conf-stat">{p.facName}</div>
        <div className="conf-stat">▸ {p.voice.name.toUpperCase()}</div>
      </div>

      <div className="df-done-actions">
        <a
          href={`/s/${p.supporterSlug}`}
          className="conf-btn-prim"
        >
          SEE YOUR CHARACTER ON THE WALL ▶
        </a>
        <a
          href={p.wallUrl || `/${p.handle}`}
          className="conf-btn-ghost"
        >
          VIEW {p.display.toUpperCase()}&apos;S WALL
        </a>
        <ShareOnX
          handle={p.handle}
          display={p.display}
          amount={p.amount}
          facName={p.facName}
        />
        <a href={`/${p.handle}/donate`} className="conf-btn-ghost" onClick={() => location.reload()}>
          DROP ANOTHER
        </a>
      </div>

      <div className="df-trailer">
        CHARGED · <span>${p.amount.toFixed(2)} USD</span>
        {import.meta.env.DEV ? <> · DEV MODE</> : null}
      </div>
    </div>
  );
}

// Static preview of the donor's picked GLB character. We render an <img> from
// the matching kenney thumbnail if one ships in /overlay-demo/assets/<group>/
// Preview/<id>.png; otherwise we fall back to a labeled badge so the donor
// still sees *something*. (The actual 3D model renders on the wall.)
function CharacterPreview({
  character,
  small = false,
}: {
  character: CharacterPick;
  small?: boolean;
}): JSX.Element {
  const [broken, setBroken] = useState(false);
  const thumb = `/overlay-demo/assets/${character.group}/Preview/${character.id}.png`;
  const cls = `df-char-thumb${small ? " sm" : ""}`;
  if (broken) {
    return (
      <div className={cls} aria-label={character.label}>
        <span className="df-char-fallback">{character.label[0] ?? "?"}</span>
      </div>
    );
  }
  return (
    <img
      className={cls}
      src={thumb}
      alt={character.label}
      onError={() => setBroken(true)}
      loading="lazy"
    />
  );
}

function ShareOnX({
  handle,
  display,
  amount,
  facName,
}: {
  handle: string;
  display: string;
  amount: number;
  facName: string;
}): JSX.Element {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://subswall.com";
  const wallUrl = `${origin}/${handle}`;
  const caption = `Just dropped a $${amount} ${facName} totem on ${display}'s wall on @subswall 🗿`;
  const intent =
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}` +
    `&url=${encodeURIComponent(wallUrl)}`;
  return (
    <a href={intent} target="_blank" rel="noreferrer" className="conf-btn-ghost">
      SHARE ON X
    </a>
  );
}
